import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import type { PayrollCalculationResult, PayrollDeduction } from './payrollCalculations';
import { COMPANY_INFO } from '@/config/companyInfo';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

/** Monthly time submission data shown at the top of every payslip. */
export interface TimeSubmissionData {
  actualHours: number;      // Ist — hours worked from time entries
  expectedHours: number;    // Soll — from calculateMonthlySoll()
  difference: number;       // Ist - Soll
  workingDays: number;      // working days in the month
  vacationHours: number;    // vacation hours taken this month
  holidays: string[];       // holiday names (e.g. ["Karfreitag", "Ostermontag"])
}

export interface SwissPayslipData {
  employee: {
    id: string;
    name: string;
    address?: string;
    ahv_number?: string;
    employee_number?: string;
  };
  company: {
    name: string;
    address: string;
    phone?: string;
    email?: string;
  };
  payroll: PayrollCalculationResult;
  period: {
    start_date: string;
    end_date: string;
    payment_date: string;
  };
  /** Monthly time submission — mandatory section shown on every payslip. */
  timeSubmission?: TimeSubmissionData;
  minimal?: boolean; // Flag for minimal rendering
}

// ─── Layout constants ──────────────────────────────────────────────
const MARGIN = 15;
const LINE_HEIGHT = 5.5;
const YELLOW_LINE_COLOR: [number, number, number] = [242, 183, 5];

/** Fetch the company logo as a base64 data URL for embedding in PDFs. */
async function fetchLogoBase64(): Promise<string | null> {
  try {
    const resp = await fetch(COMPANY_INFO.logoPath);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Render multi-line text (splits on \n) and advance yPosition correctly.
 * Returns the new yPosition after the last line.
 */
function addMultiLineText(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  lineHeight = LINE_HEIGHT
): number {
  const lines = text.split('\n');
  for (const line of lines) {
    pdf.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

export async function generateSwissPayslip(data: SwissPayslipData): Promise<Blob> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.width;
  const contentWidth = pageWidth - MARGIN * 2;
  let y = MARGIN;

  // ── Logo (top-left) ──
  const logoData = await fetchLogoBase64();
  if (logoData) {
    // Logo aspect ratio ~2.8:1 (1117x399). Render at 50mm width.
    const logoW = 50;
    const logoH = logoW / 2.8;
    pdf.addImage(logoData, 'JPEG', MARGIN, y, logoW, logoH);
    y += logoH + 4;
  } else {
    // Fallback: company name as text header
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(data.company.name, MARGIN, y + 6);
    y += 14;
  }

  // ── Thin yellow accent line below logo ──
  pdf.setDrawColor(YELLOW_LINE_COLOR[0], YELLOW_LINE_COLOR[1], YELLOW_LINE_COLOR[2]);
  pdf.setLineWidth(0.8);
  pdf.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 8;

  // ── Title ──
  const periodLabel = format(new Date(data.period.start_date), 'MMMM yyyy', { locale: de });
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Lohnabrechnung ${periodLabel}`, MARGIN, y);
  y += 10;

  // ── Side-by-side: Arbeitgeber (left) | Arbeitnehmer (right) ──
  const colLeft = MARGIN;
  const colRight = pageWidth / 2 + 5;

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(120);
  pdf.text('ARBEITGEBER', colLeft, y);
  pdf.text('ARBEITNEHMER/IN', colRight, y);
  pdf.setTextColor(0);
  y += 5;

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');

  // Left column: company info
  let yLeft = y;
  pdf.setFont('helvetica', 'bold');
  yLeft = addMultiLineText(pdf, data.company.name, colLeft, yLeft);
  pdf.setFont('helvetica', 'normal');
  yLeft = addMultiLineText(pdf, data.company.address, colLeft, yLeft);

  // Right column: employee info
  let yRight = y;
  yRight = addMultiLineText(pdf, data.employee.name, colRight, yRight);
  if (data.employee.address) {
    yRight = addMultiLineText(pdf, data.employee.address, colRight, yRight);
  }
  if (data.employee.ahv_number) {
    pdf.text(`AHV-Nr: ${data.employee.ahv_number}`, colRight, yRight);
    yRight += LINE_HEIGHT;
  }

  // Advance y past the taller column
  y = Math.max(yLeft, yRight) + 8;

  // ── Monatsabschluss (monthly time submission) ──
  if (data.timeSubmission) {
    const ts = data.timeSubmission;
    const diffSign = ts.difference >= 0 ? '+' : '';

    const tsBody: string[][] = [
      ['Arbeitstage', `${ts.workingDays} Tage`],
      ['Ist-Stunden', `${ts.actualHours.toFixed(2)} h`],
      ['Soll-Stunden', `${ts.expectedHours.toFixed(2)} h`],
      ['Differenz', `${diffSign}${ts.difference.toFixed(2)} h`],
    ];
    if (ts.vacationHours > 0) {
      tsBody.push(['Ferien', `${ts.vacationHours.toFixed(2)} h`]);
    }
    if (ts.holidays.length > 0) {
      tsBody.push(['Feiertage', ts.holidays.join(', ')]);
    }

    pdf.autoTable({
      startY: y,
      head: [['Monatsabschluss', '']],
      body: tsBody,
      headStyles: {
        fillColor: [50, 50, 50],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: contentWidth / 2, fontStyle: 'bold' },
        1: { cellWidth: contentWidth / 2, halign: 'right' },
      },
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: contentWidth,
    });

    y = (pdf as any).lastAutoTable.finalY + 10;
  }

  // ── Payroll summary ──
  const summaryBody: string[][] = [];
  if (!data.minimal && data.payroll.hours_worked > 0) {
    summaryBody.push(['Arbeitsstunden', `${data.payroll.hours_worked.toFixed(1)} h`]);
  }
  summaryBody.push(
    ['Bruttogehalt', `CHF ${data.payroll.gross_salary.toFixed(2)}`],
    ['Abzüge Total', `– CHF ${data.payroll.total_deductions.toFixed(2)}`],
  );

  pdf.autoTable({
    startY: y,
    head: [['Lohnabrechnung', 'Betrag']],
    body: summaryBody,
    foot: [['Nettogehalt', `CHF ${data.payroll.net_salary.toFixed(2)}`]],
    headStyles: {
      fillColor: YELLOW_LINE_COLOR,
      textColor: [30, 30, 30],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: { fontSize: 9 },
    footStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 10,
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.6 },
      1: { cellWidth: contentWidth * 0.4, halign: 'right' },
    },
    margin: { left: MARGIN, right: MARGIN },
    tableWidth: contentWidth,
  });

  y = (pdf as any).lastAutoTable.finalY + 10;

  // ── Deductions breakdown ──
  if (data.payroll.deductions && data.payroll.deductions.length > 0) {
    if (data.minimal) {
      const rows = data.payroll.deductions.map((d: PayrollDeduction) => [
        d.description,
        `CHF ${d.deduction_amount.toFixed(2)}`,
      ]);

      pdf.autoTable({
        startY: y,
        head: [['Abzug', 'Betrag']],
        body: rows,
        headStyles: {
          fillColor: [220, 220, 220],
          textColor: 0,
          fontStyle: 'bold',
          fontSize: 9,
        },
        bodyStyles: { fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.7 },
          1: { cellWidth: contentWidth * 0.3, halign: 'right' },
        },
        margin: { left: MARGIN, right: MARGIN },
        tableWidth: contentWidth,
      });
    } else {
      const rows = data.payroll.deductions.map((d: PayrollDeduction) => [
        d.description,
        `${d.rate_percent.toFixed(2)}%`,
        `CHF ${d.base_amount.toFixed(2)}`,
        `CHF ${d.deduction_amount.toFixed(2)}`,
      ]);

      pdf.autoTable({
        startY: y,
        head: [['Abzug', 'Satz', 'Basis', 'Betrag']],
        body: rows,
        headStyles: {
          fillColor: [220, 220, 220],
          textColor: 0,
          fontStyle: 'bold',
          fontSize: 9,
        },
        bodyStyles: { fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.4 },
          1: { cellWidth: contentWidth * 0.15, halign: 'center' },
          2: { cellWidth: contentWidth * 0.225, halign: 'right' },
          3: { cellWidth: contentWidth * 0.225, halign: 'right' },
        },
        margin: { left: MARGIN, right: MARGIN },
        tableWidth: contentWidth,
      });
    }

    y = (pdf as any).lastAutoTable.finalY + 10;
  }

  // ── Payment information ──
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(120);
  pdf.text('ZAHLUNGSINFORMATIONEN', MARGIN, y);
  pdf.setTextColor(0);
  y += 5;

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Auszahlungsdatum: ${format(new Date(data.period.payment_date), 'dd.MM.yyyy')}`, MARGIN, y);
  y += LINE_HEIGHT;
  pdf.text(
    `Lohnperiode: ${format(new Date(data.period.start_date), 'dd.MM.yyyy')} – ${format(new Date(data.period.end_date), 'dd.MM.yyyy')}`,
    MARGIN, y
  );

  // ── Footer ──
  const pageHeight = pdf.internal.pageSize.height;

  // Yellow accent bar at the very bottom
  pdf.setFillColor(YELLOW_LINE_COLOR[0], YELLOW_LINE_COLOR[1], YELLOW_LINE_COLOR[2]);
  pdf.rect(0, pageHeight - 5, pageWidth, 5, 'F');

  // Footer text above the bar
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(140);
  pdf.text(
    `Erstellt am ${format(new Date(), 'dd.MM.yyyy HH:mm')} · Diese Lohnabrechnung entspricht den schweizerischen Bestimmungen.`,
    pageWidth / 2,
    pageHeight - 9,
    { align: 'center' }
  );
  pdf.setTextColor(0);

  return pdf.output('blob');
}

export function downloadPayslip(payslipBlob: Blob, employeeName: string, period: string) {
  const url = window.URL.createObjectURL(payslipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Lohnabrechnung_${employeeName.replace(/\s+/g, '_')}_${period}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
